import type { TravelPartyMember } from "../types";

/**
 * Helpers for writing only what an edit actually changed.
 *
 * Firestore offline is last-write-wins per field. A form that writes back every
 * field it holds — values copied when it opened, possibly hours ago on a ship
 * with no signal — silently reverts whatever anyone else changed in the
 * meantime: the order someone typed at the same dinner, the member who joined
 * the trip, the location-sharing toggle. Diffing against what the form opened
 * with, and writing only the difference, leaves everyone else's changes alone.
 */

/**
 * Structural equality for the values the app stores: primitives, arrays, plain
 * objects and Firestore `Timestamp`s (compared by their own `seconds` /
 * `nanoseconds` fields, as any class instance is). `undefined` and a missing
 * key count as equal, since Firestore stores neither.
 */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => deepEqual(v, b[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(ao), ...Object.keys(bo)]);
  for (const k of keys) {
    if (!deepEqual(ao[k], bo[k])) return false;
  }
  return true;
}

/** Keys of `after` whose value differs from `before`'s. */
export function changedKeys<T extends Record<string, unknown>>(before: T, after: T): (keyof T)[] {
  return (Object.keys(after) as (keyof T)[]).filter((k) => !deepEqual(before[k], after[k]));
}

/** Just the entries of `after` that differ from `before` — a ready-made patch. */
export function changedFields<T extends Record<string, unknown>>(before: T, after: T): Partial<T> {
  const patch: Partial<T> = {};
  for (const k of changedKeys(before, after)) patch[k] = after[k];
  return patch;
}

/**
 * A shallow copy without `undefined` values. Firestore rejects `undefined`
 * outright, and — worse for `arrayRemove` — an object carrying one would never
 * compare equal to what's stored.
 */
export function withoutUndefined<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

export interface TravelPartyEdit {
  /** Members to write whole, at `travelParty.<key>` — the ones this edit added. */
  set: Record<string, TravelPartyMember>;
  /** Member keys to delete — the ones this edit removed. */
  remove: string[];
  /** For `arrayUnion` on `memberUserIds`. */
  addUserIds: string[];
  /** For `arrayRemove` on `memberUserIds`. */
  removeUserIds: string[];
}

/**
 * What a trip-form save should write for the travel party, given the party the
 * form opened with, the party it's submitting, and the party as it is now.
 *
 * The form only adds and removes members — it has no in-place member edit — so
 * members present on both sides are never written. That's what keeps a
 * `locationSharingEnabled` toggle, or an accepted invite filling in `userId`,
 * from being reverted by a form opened before it happened. Anyone added to the
 * trip by someone else meanwhile isn't in either of the form's copies, so they
 * aren't touched either.
 *
 * `current` is used to skip work someone else already did (a member they
 * already added or removed) and to find the `userId` of a member being removed,
 * which may have been linked since the form opened.
 */
export function travelPartyEdit(
  opened: Record<string, TravelPartyMember>,
  edited: Record<string, TravelPartyMember>,
  current: Record<string, TravelPartyMember>,
): TravelPartyEdit {
  const set: Record<string, TravelPartyMember> = {};
  for (const [key, member] of Object.entries(edited)) {
    if (!(key in opened) && !(key in current)) set[key] = member;
  }
  const remove = Object.keys(opened).filter((key) => !(key in edited) && key in current);

  // A userId stays in memberUserIds while any member left on the trip still
  // carries it — the same person can't be dropped by removing a duplicate.
  const removing = new Set(remove);
  const remaining = new Set(
    [
      ...Object.entries(current).filter(([key]) => !removing.has(key)).map(([, m]) => m.userId),
      ...Object.values(set).map((m) => m.userId),
    ].filter((id): id is string => id != null),
  );
  const removeUserIds = [
    ...new Set(
      remove
        .map((key) => current[key]?.userId ?? opened[key]?.userId ?? null)
        .filter((id): id is string => id != null && !remaining.has(id)),
    ),
  ];
  const addUserIds = [
    ...new Set(Object.values(set).map((m) => m.userId).filter((id): id is string => id != null)),
  ];
  return { set, remove, addUserIds, removeUserIds };
}
